import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Sidebar from '../Sidebar';
import Header from '../Header';
import { useAuth } from '../context/AuthContext';

// Define the type for location state
interface LocationState {
  type: 'administrator' | 'underwriter';
  insurerId: number;
  insurerName: string;
  insurerStatus: string;
}

const ApplicationCategories = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);

  // Get the passed data from navigation
  const state = location.state as LocationState;
  const { type, insurerId, insurerName, insurerStatus } = state || {};

  // Add a check to ensure state exists
  useEffect(() => {
  // Use a timeout to allow state to settle
  const timer = setTimeout(() => {
    const state = location.state as LocationState;
    console.log('State check after delay:', {
      locationState: location.state,
      insurerId: state?.insurerId,
      insurerName: state?.insurerName,
      hasState: !!location.state
    });
    
    if (!location.state || !state?.insurerId) {
      console.error('Missing required state after delay. Redirecting...');
      navigate('/application-insurers');
    }
  }, 100); // Wait 100ms for state to settle

  return () => clearTimeout(timer);
}, [location.state, navigate]);

  const toggleSidebar = () => {
    setIsSidebarVisible(!isSidebarVisible);
  };

  // Application categories buttons
  const categoryButtons = [
    { name: 'Property', icon: '🏠' },
    { name: 'Household Contents', icon: '🛋️' },
    { name: 'Vehicle', icon: '🚗' },
    { name: 'Trailer/Caravan', icon: '🚐' },
    { name: 'All Risk', icon: '📦' },
    { name: 'Water Craft', icon: '🛥️' },
    { name: 'Value Added', icon: '➕' },
    { name: 'Broker Consent', icon: '✅' },
    { name: 'Consent Clause', icon: '📋' },
    { name: 'Disclosure', icon: '📄' },
    { name: 'Debit Order', icon: '💳' },
    { name: 'Important', icon: '⚠️' },
    { name: 'Fees', icon: '💰' },
    { name: 'Service Provider', icon: '👷' },
    { name: 'Declaration', icon: '📝' },
    { name: 'Management Fees Declaration', icon: '📊' },
    { name: 'Signature', icon: '✍️' },
  ];

  // Handle category button click
  const handleCategoryClick = (categoryName: string) => {
  const state = location.state as LocationState;
  
  if (!state?.insurerId || !state?.insurerName) {
    console.error('Cannot navigate: Missing insurer data', { 
      state,
      insurerId: state?.insurerId,
      insurerName: state?.insurerName 
    });
    return;
  }
  
  const navigationState = {
    type: state.type || 'administrator',
    insurerId: state.insurerId,
    insurerName: state.insurerName,
    insurerStatus: state.insurerStatus || 'Active',
    category: categoryName
  };
  
  console.log('Navigating with state:', navigationState);
  
  navigate('/application-questions', {
    state: navigationState
  });
};

  return (
    <>
      <Sidebar 
        isVisible={isSidebarVisible} 
        toggleSidebar={toggleSidebar} 
      />

      <div className={`transition-all duration-300 ${isSidebarVisible ? 'ml-64' : 'ml-0'}`}>
        <Header 
          title="Application Categories"
          user={user}
          toggleSidebar={toggleSidebar}
          isSidebarVisible={isSidebarVisible}
          showClientNumber={false}
        />

        <div className="min-h-screen bg-gray-50 p-4 md:p-6">
          <div className="max-w-4xl mx-auto">
            {/* Page Header */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Application Categories</h1>
                  <div className="mt-2 flex items-center">

                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      (state?.type || 'administrator') === 'administrator' 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-purple-100 text-purple-800'
                    }`}>
                      {(state?.type || 'administrator') === 'administrator' ? 'Administrator' : 'Underwriter'}
                    </span>
                    <span className="mx-3 text-gray-400">•</span>
                    <span className="text-lg font-semibold text-gray-900">{state?.insurerName || 'Unknown'}</span>
                    <span className="mx-3 text-gray-400">•</span>
                    <span className="flex items-center">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-1.5"></span>
                      <span className="text-sm text-gray-600">{state?.insurerStatus || 'Unknown'}</span>
                    </span>

                  </div>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => navigate('/application-insurers')}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    ← Back to Insurers
                  </button>
                  <button
                    onClick={() => navigate('/process-definitions')}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Back to Process Definitions
                  </button>
                </div>
              </div>
              <p className="text-gray-600">
                Manage application categories for {type || 'administrator'} <span className="font-semibold">"{insurerName || 'Unknown'}"</span>. 
                Click on any category to configure its settings.
              </p>
            </div>

            {/* Categories Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categoryButtons.map((category, index) => (
                <button
                  key={index}
                  onClick={() => handleCategoryClick(category.name)}
                  className="p-5 bg-white border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-all duration-200 shadow-sm flex flex-col items-center justify-center text-center"
                >
                  <div className="text-3xl mb-3">{category.icon}</div>
                  <div className="text-gray-900 font-medium">{category.name}</div>
                  <div className="mt-2 text-sm text-gray-500">
                    Click to configure
                  </div>
                </button>
              ))}
            </div>

            {/* Additional Information */}
            <div className="mt-8 p-5 bg-white rounded-lg shadow border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">About Application Categories</h3>
              <div className="text-gray-600 space-y-2">
                <p>
                  Each category represents a different aspect of the insurance application process 
                  for <span className="font-medium">{state?.type || 'administrator'} "{state?.insurerName || 'Unknown Insurer'}"</span>.
                </p>
                <p>
                  Configure settings, requirements, and workflows for each category to customize 
                  the application experience.
                </p>
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-500">
                    <span className="font-medium">Note:</span> Changes made here will only affect applications 
                    for this specific {type}.
                  </p>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg shadow p-4 text-center">
                <div className="text-2xl font-bold text-blue-600">{categoryButtons.length}</div>
                <div className="text-sm text-gray-600">Total Categories</div>
              </div>
              <div className="bg-white rounded-lg shadow p-4 text-center">
                <div className="text-2xl font-bold text-green-600">0</div>
                <div className="text-sm text-gray-600">Configured</div>
              </div>
              <div className="bg-white rounded-lg shadow p-4 text-center">
                <div className="text-2xl font-bold text-yellow-600">{categoryButtons.length}</div>
                <div className="text-sm text-gray-600">Pending Setup</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ApplicationCategories;